// WHERE AN EXTERNAL LEARNER'S SIGN-IN LINK LANDS (task ec54522c), and the one place its `returnTo` is
// validated.
//
// WHY IT IS NOT JUST `params.get("returnTo")`. A questionnaire invitation to a learner whose only door is a
// mailed link now carries the redeem route `/t/<slug>/login/email/redeem` with two query parameters — the
// raw one-time token, and a `returnTo` of `/questionnaires/<id>/respond` — so the redeem mints the session
// and goes straight to the form instead of dropping them on the home view.
// That parameter arrives in a URL anybody can compose and is passed on to `POST /api/auth/email-link/request`
// when the learner asks for a fresh link — i.e. it can end up deciding where a mail somebody ELSE caused to
// be sent takes its reader. So the accepted set is exactly one shape, a questionnaire respond path, and
// everything else is DROPPED rather than rendered: an unrecognised value costs the learner one click on the
// Kyselyt tab, while "any in-app path" would be an attacker-chosen landing page.
//
// The same rule, in the same words, is `QuestionnaireLinks.TryParseRespondPath` on the backend
// (backend/src/Clms.Infrastructure/CoreServices/PublicAppOptions.cs), which is what the request endpoint
// applies before baking the value into the mailed URL. Two copies, deliberately: this one decides what the
// browser navigates to, that one decides what goes into an email, and neither may trust the other's input.

/** The app-relative path of a questionnaire's respondent form — the tail of the mailed link. */
export function respondPath(questionnaireId: string): string {
  return `/questionnaires/${encodeURIComponent(questionnaireId)}/respond`;
}

const RESPOND_PATH = /^\/questionnaires\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/respond$/;

/**
 * The value back, when it is exactly a questionnaire respond path; `null` for anything else — another
 * in-app path, an absolute URL, a protocol-relative `//host`, a backslash, trailing junk. Never throws and
 * never repairs: a value that is not this shape has no safe reading.
 */
export function parseRespondPath(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || !RESPOND_PATH.test(trimmed)) return null;
  return trimmed;
}

/**
 * `path` with a validated `returnTo` appended, or `path` unchanged when there is none to carry. Used for
 * every way OUT of the redeem page's refusals: the learner who asks for a fresh link is still on their way
 * to a questionnaire, and that is the destination this keeps.
 */
export function withReturnTo(path: string, returnTo: string | null | undefined): string {
  const safe = parseRespondPath(returnTo);
  if (!safe) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}returnTo=${encodeURIComponent(safe)}`;
}
